import { useState } from 'react';

import { FcCalendar } from 'react-icons/fc';
import styled from 'styled-components';

import { ColumnType, DeliveryState, GetDeliveryQuery } from 'data/__generated__';

import { DetailValue } from 'components/DetailsView/DetailValue';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { StatusTag } from '../components/StatusTag';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { CopyValue } from 'components/CopyValue';
import { UpdateDeliveryDatePopup } from './UpdateDeliveryDatePopup';

import { formatDateAsAnniversary, getExtendedFormattedDatetime } from '../../../helpers/dateTimes';
import { DetailLink } from 'components/DetailsView';
import { OrderDeliveryNote } from '../components/OrderDeliveryNote';

type DeliveredColumn = NonNullable<GetDeliveryQuery['delivery']>['deliveredMicrostoreColumns'][number];

// Secured/Unsecured microstore columns have a dedicated MS Colonne detail page; other types
// (e.g. VirtualMicrostore) aren't served by it, so fall back to the generic column stocks page.
const getColumnDetailPath = ({ _id, type }: DeliveredColumn): string =>
    type === ColumnType.SecuredMicrostore || type === ColumnType.UnsecuredMicrostore
        ? `/microstore-column/${_id}`
        : `/column/${_id}/stocks`;

export const GeneralInfoSection = ({ delivery }: { delivery: NonNullable<GetDeliveryQuery['delivery']> }) => {
    const {
        _id: deliveryId,
        createdAt,
        deliveryDate,
        isDynamic,
        state,
        trackingUrl,
        updatedAt,
        label,
        orders,
        siteId,
        deliveredMicrostoreColumns,
    } = delivery;
    const [isUpdateDatePopupOpen, setIsUpdateDatePopupOpen] = useState(false);

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <CopyValue value={deliveryId} />
                    <DetailValue
                        label="Détail"
                        value={
                            <span className="inline-flex items-center gap-1.5">
                                {label}
                                <DynamicDeliveryTag isDynamic={isDynamic} label="Livraison dynamique" />
                            </span>
                        }
                    />
                    <DetailValue
                        label="Date de livraison"
                        value={
                            <DeliveryDateContainer>
                                <FcCalendar size={22} />
                                <DeliveryDate>
                                    {formatDateAsAnniversary({ dateTime: deliveryDate, displayDay: true })}
                                </DeliveryDate>
                                {[
                                    DeliveryState.Created,
                                    DeliveryState.Packaging,
                                    DeliveryState.Ready,
                                    DeliveryState.NotDelivered,
                                    DeliveryState.InProgress,
                                ].includes(state) ? (
                                    <UpdateDateButton onClick={() => setIsUpdateDatePopupOpen(true)}>
                                        Modifier
                                    </UpdateDateButton>
                                ) : null}
                            </DeliveryDateContainer>
                        }
                    />
                    <UpdateDeliveryDatePopup
                        deliveryId={deliveryId}
                        isOpen={isUpdateDatePopupOpen}
                        setIsOpen={setIsUpdateDatePopupOpen}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                    <DetailValue label="Dernière mise à jour" value={getExtendedFormattedDatetime(updatedAt)} />
                    <StatusTag state={state} showHeading />
                </SectionColumn>
                <SectionColumn>
                    {orders.map((order) => (
                        <DetailLink key={order._id} path="order" label="Order ID" value={order._id} />
                    ))}
                    <DetailLink path="site" label="Site ID" value={siteId} />
                    {deliveredMicrostoreColumns.map((column, index) => (
                        <DetailLink
                            key={column._id}
                            label={index === 0 ? 'Colonnes livrées' : undefined}
                            name={column.name}
                            to={getColumnDetailPath(column)}
                        />
                    ))}
                    <OrderDeliveryNote delivery={delivery} />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const DeliveryDateContainer = styled.div`
    display: flex;
    align-items: center;
`;

const DeliveryDate = styled.span`
    margin-left: 5px;
`;

const UpdateDateButton = styled(TotemPrimaryButton)`
    margin-left: 15px;
`;
